import { useState, useEffect, useMemo, useCallback } from 'react';

export const useDraftTimer = (socket) => {
  const [timerData, setTimerData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Client-side countdown effect
  useEffect(() => {
    if (!timerData) {
      setTimeRemaining(0);
      return;
    }

    const startTime = new Date(timerData.started_at).getTime();
    const duration = timerData.duration_seconds;

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;
      const remaining = Math.max(0, duration - elapsed);
      setTimeRemaining(remaining);
    };

    // Initial update
    updateTimer();

    // Update every 100ms for smooth countdown
    const interval = setInterval(updateTimer, 100);

    return () => clearInterval(interval);
  }, [timerData]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleTimerStart = (data) => {
      setTimerData({
        started_at: data.started_at,
        expires_at: data.expires_at,
        duration_seconds: data.duration_seconds
      });
    };

    const handleTimerExpired = () => {
      setTimeRemaining(0);
      setTimerData(null);
    };

    socket.on('timer-start', handleTimerStart);
    socket.on('timer-expired', handleTimerExpired);

    return () => {
      socket.off('timer-start', handleTimerStart);
      socket.off('timer-expired', handleTimerExpired);
    };
  }, [socket]);

  // Display formatting
  const displayTime = useMemo(() => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = Math.floor(timeRemaining % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [timeRemaining]);

  // Percentage remaining
  const percentRemaining = useMemo(() => {
    if (!timerData) return 0;
    return (timeRemaining / timerData.duration_seconds) * 100;
  }, [timeRemaining, timerData]);

  // Urgency check (< 30 seconds)
  const isUrgent = useMemo(() => {
    return timerData && timeRemaining > 0 && timeRemaining <= 30;
  }, [timerData, timeRemaining]);

  // Helper functions
  const resetTimer = useCallback((duration) => {
    setTimerData({
      started_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + duration * 1000).toISOString(),
      duration_seconds: duration
    });
  }, []);

  const clearTimer = useCallback(() => {
    setTimerData(null);
    setTimeRemaining(0);
  }, []);

  return {
    timerData,
    timeRemaining,
    isExpired: timerData !== null && timeRemaining <= 0,
    setTimerData,
    resetTimer,
    clearTimer,
    displayTime,
    isActive: timerData !== null && timeRemaining > 0,
    percentRemaining,
    isUrgent
  };
};
